// Local override of react_ujs/src/renderHelpers.js
// During the React 16 → 18 migration, React 18's hydrateRoot() is stricter
// about hydration mismatches than React 16's hydrate(). Pre-existing
// mismatches that React 16 silently patched now throw hard errors.
//
// This override passes onRecoverableError to hydrateRoot() so the app
// gracefully falls back to client rendering instead of showing an error overlay.
//
// It also wraps createRoot().render() in flushSync() so that DOM updates are
// committed synchronously, matching React 16's ReactDOM.render() behavior.
// This is required because react_ujs expects the DOM to be ready immediately
// after mountComponents() returns.

const ReactDOMClient = require('react-dom/client')

const ReactDOM = require('react-dom')

const { flushSync } = ReactDOM


// Track createRoot/hydrateRoot instances per DOM node so we can call
// root.unmount() later. WeakMap avoids memory leaks — when the DOM node
// is garbage collected, the root reference is too.
const rootsMap = new WeakMap()
const fallbackNodes = new WeakSet()

export function supportsHydration() {
  return typeof ReactDOMClient.hydrateRoot === 'function' || typeof ReactDOM.hydrate === 'function'
}

// Fall back from failed hydration to a full client render.
// Clears the container's SSR markup and mounts from scratch so the page
// stays functional even when the server/client trees diverge.
function clientFallback(node, component) {
  console.warn('[react-rails] Hydration failed – falling back to client render')
  const existingRoot = rootsMap.get(node)
  if (existingRoot) {
    try { existingRoot.unmount() } catch (_e) { /* already broken */ }
    rootsMap.delete(node)
  }
  // Remove stale SSR children so createRoot starts fresh.
  while (node.firstChild) {
    node.removeChild(node.firstChild)
  }
  const freshRoot = ReactDOMClient.createRoot(node)
  rootsMap.set(node, freshRoot)
  flushSync(() => {
    freshRoot.render(component)
  })
}

function scheduleClientFallback(node, component) {
  if (fallbackNodes.has(node)) {
    return
  }

  fallbackNodes.add(node)

  setTimeout(() => {
    fallbackNodes.delete(node)

    if (!node.isConnected) {
      rootsMap.delete(node)
      return
    }

    clientFallback(node, component)
  }, 0)
}

export function reactHydrate(node, component) {
  if (typeof ReactDOMClient.hydrateRoot === 'function') {
    // react_ujs may mount the same SSR node again later in the page lifecycle.
    // Reuse the existing root instead of calling hydrateRoot twice.
    const existingRoot = rootsMap.get(node)

    if (existingRoot) {
      flushSync(() => {
        existingRoot.render(component)
      })

      return existingRoot
    }

    try {
      const root = ReactDOMClient.hydrateRoot(node, component, {
        onRecoverableError(error) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[react-rails] Hydration mismatch:', error.message)
          }

          scheduleClientFallback(node, component)
        },
      })
      rootsMap.set(node, root)
      return root
    } catch (error) {
      // hydrateRoot itself can throw synchronously on severe mismatches.
      console.error('[react-rails] hydrateRoot threw:', error.message)
      clientFallback(node, component)
      return rootsMap.get(node)
    }
  }

  return ReactDOM.hydrate(component, node)
}

export function createReactRootLike(node) {
  if (ReactDOMClient.createRoot) {
    // react_ujs may call mountComponents() more than once on the same node.
    // Reuse the existing root to avoid duplicate mounts and side-effects.
    const existingRoot = rootsMap.get(node)
    if (existingRoot) {
      return {
        render(component) {
          flushSync(() => {
            existingRoot.render(component)
          })
        },
      }
    }

    const root = ReactDOMClient.createRoot(node)
    rootsMap.set(node, root)
    return {
      render(component) {
        flushSync(() => {
          root.render(component)
        })
      },
    }
  }

  return legacyReactRootLike(node)
}

function legacyReactRootLike(node) {
  return {
    render(component) {
      ReactDOM.render(component, node)
    },
  }
}

export function unmountReactRoot(node) {
  const root = rootsMap.get(node)
  if (root) {
    root.unmount()
    rootsMap.delete(node)
    return true
  }
  return false
}
